import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import React, {SyntheticEvent, useEffect, useMemo, useState} from "react";

import {useCancelableEffect} from "modules/common/hooks/useCancelableEffect";
import R from "resources/Namespace";
import {register} from "serviceWorkerRegistration";
import {useFluxSnackbar} from "components/common/hooks/useFluxSnackbar";

const DismissedSessionKey = "appUpdateDismissed";

function AppUpdateSnackbar() {
    const {enqueueSnackbar, closeSnackbar} = useFluxSnackbar();
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

    const showReload = useMemo(() => !!waitingWorker, [waitingWorker]);

    useCancelableEffect(({mounted}) => {
        let currentRegistration: ServiceWorkerRegistration | null = null;

        // Don't bother registering the service worker if the user has already dismissed the snackbar
        if (sessionStorage.getItem(DismissedSessionKey)) {
            return;
        }

        register({
            onUpdateFound: (registration: ServiceWorkerRegistration) => {
                if (!mounted) {
                    return;
                }

                currentRegistration = registration;

                setWaitingWorker(registration.waiting);
            },
        });
    }, []);

    useEffect(() => {
        // Sync session storage with showReload state
        if (showReload) {
            sessionStorage.removeItem(DismissedSessionKey);
        } else {
            sessionStorage.setItem(DismissedSessionKey, "true");
        }

        if (!showReload) {
            return;
        }

        const onReload = () => {
            if (waitingWorker) {
                waitingWorker.postMessage({type: "SKIP_WAITING"});
            }

            setWaitingWorker(null);

            window.location.reload();
        };

        const onClose = (_: SyntheticEvent | MouseEvent, reason?: string) => {
            if (reason === "clickaway") {
                return;
            }

            setWaitingWorker(null);
        };

        const snackbarKey = enqueueSnackbar(R.strings.snackbar.app_update.new_version, "appUpdate", {
            action: (
                <>
                    {/* eslint-disable-next-line react-memo/require-usememo */}
                    <Button color="primary" size="small" onClick={onReload}>
                        {R.strings.snackbar.app_update.reload_page}
                    </Button>
                    <IconButton
                        size="small"
                        aria-label={R.strings.snackbar.app_update.close}
                        color="inherit"
                        //  eslint-disable-next-line react-memo/require-usememo
                        onClick={onClose}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </>
            ),
            persist: true,
            preventDuplicate: true,
            anchorOrigin: {
                vertical: "top",
                horizontal: "center",
            },
        });

        return () => {
            closeSnackbar(snackbarKey);
        };
    }, [enqueueSnackbar, closeSnackbar, showReload, waitingWorker]);

    return null;
}

export default React.memo(AppUpdateSnackbar);
